import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import PolicyDataContainer from 'features/insuranceDocument/edit/policy/policyData/components/PolicyDataContainer';
import OfferDataContainer from './policyOfferData/OfferDataContainer';
import { POLICY_DATA_QUERY } from 'features/insuranceDocument/edit/policy/policyData/queries';
import { useTranslation } from 'react-i18next';
import { useChangeTrackingLens, get } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { INSURANCE_DATA_QUERY, INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { useDictionaryHandling, useQueryWithSimpleErrorHandling, useInsuranceTypeConfiguration } from 'hooks';
import { Prompt } from 'react-router-dom';
import { insuranceTypeNames } from 'constants/defaultValues';

const InsurancePolicyContainer = ({ documentId, contextRef }) => {
  const { t } = useTranslation();
  const { rcaInsuranceTypeConfiguration, cascoInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(
    new Array(insuranceTypeNames.RCA, insuranceTypeNames.CASCO)
  );
  const [policyDataLens, policyDirtyInfo, resetPolicyDataLens] = useChangeTrackingLens({});

  const { loading: policyDataLoading } = useQueryWithSimpleErrorHandling(POLICY_DATA_QUERY, {
    variables: { documentId: documentId },
    onCompleted: data => {
      resetPolicyDataLens({ ...data?.policyData });
    },
    skip: !documentId,
    notifyOnNetworkStatusChange: true
  });

  const [insuranceData, setInsuranceData] = useState({});
  const { getCodeById } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(() => getCodeById(insuranceData.insuranceTypeId), [getCodeById, insuranceData.insuranceTypeId]);
  useQueryWithSimpleErrorHandling(INSURANCE_DATA_QUERY, {
    variables: { documentId },
    onCompleted: data => {
      setInsuranceData(data?.insuranceData);
    }
  });

  return (
    <>
      <Prompt when={isDirty(policyDirtyInfo)} message={() => t('LeavingWithoutSaving')} />
      <OfferDataContainer
        documentId={documentId}
        insuredId={policyDataLens.insuredId |> get}
        clientId={policyDataLens.clientId |> get}
        negotiatedQuotation={policyDataLens.negotiatedQuotation |> get}
        insuranceTypeCode={insuranceTypeCode}
        rcaInsuranceTypeCode={rcaInsuranceTypeConfiguration?.PropertyCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeConfiguration?.PropertyCode}
        insuranceData={insuranceData}
      />
      <PolicyDataContainer
        policyDataLens={policyDataLens}
        policyDataLoading={policyDataLoading}
        policyDirtyInfo={policyDirtyInfo}
        resetPolicyLens={resetPolicyDataLens}
        rcaInsuranceTypeCode={rcaInsuranceTypeConfiguration?.PropertyCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeConfiguration?.PropertyCode}
        contextRef={contextRef}
      />
    </>
  );
};

InsurancePolicyContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  contextRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default InsurancePolicyContainer;
